import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import TradingActionBoard from './TradingActionBoard';
import IstClock from './IstClock';
import { setChargeProduct } from './equity-delivery-charges';
import './NormalTrading.css';
import './NormalTradingV1.css';

const API = '/api/bestse';
const POLL_MS = 2000;

export default function Bestse() {
  const [board, setBoard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const inFlight = useRef(false);

  const load = useCallback(async (warm = false) => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const res = await axios.get(`${API}/live-board`, {
        timeout: 20000,
        params: warm ? { warm: 1 } : undefined
      });
      setBoard(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Board load failed');
    } finally {
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    setChargeProduct('intraday');
    load(true);
    const t = setInterval(() => load(false), POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const wipe = useCallback(async () => {
    if (!window.confirm('Wipe BESTSE desk?\n\nClears BUY cards and today\'s sold rows.\nResets ₹2 lakh capital.')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/wipe`, {}, { timeout: 15000 });
      await load(true);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Wipe failed');
    } finally {
      setBusy(false);
    }
  }, [load]);

  const lists = board?.actionLists || {};
  const buy = lists.buy || [];
  const soldToday = lists.soldToday || [];
  const cap = board?.capital || {};
  const basket = board?.basket || {};
  const settings = board?.settings || {};
  const capitalAmount = Number(cap.dailyTradingCapital || settings.capitalAmount || 200000);

  const activeStep = useMemo(() => {
    if (buy.length) return 1;
    return 1;
  }, [buy.length]);

  return (
    <div className="nt-desktop nt-v1 nt-no-sidebar bestse-screen">
      <main className="nt-main">
        <header className="nt-header nt-header-compact">
          <div>
            <h1>BESTSE</h1>
            <p className="nt-subtitle">
              ₹{(capitalAmount / 100000).toFixed(0)}L capital · <strong>all day-gainers</strong> on BUY · qty 1
            </p>
            <p className="nt-subtitle" style={{ marginTop: 4 }}>
              New BUY from <strong>9:25 AM IST</strong> · profit <strong>₹{Number(settings.profitTakeMinRs || 100).toFixed(0)}–₹{Number(settings.profitTakeMaxRs || 200).toFixed(0)}</strong> → Sold Today · remaining names square-off at <strong>3:15 PM IST</strong>
            </p>
          </div>
          <div className="nt-v1-master-clock">
            <IstClock />
          </div>
          <div className="nt-v1-header-links" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <Link to="/BestPerformer" className="nt-v1-btn">Best Performer</Link>
            <Link to="/Report?screen=bestse" className="nt-v1-btn">Report</Link>
            <button type="button" className="nt-v1-btn primary" onClick={() => load(true)} disabled={busy}>
              Refresh
            </button>
            <button type="button" className="nt-v1-btn danger" onClick={wipe} disabled={busy}>
              Wipe desk
            </button>
          </div>
        </header>

        {error && <div className="nt-v1-master-error">{error}</div>}

        <div className="nt-v1-status-strip" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
          <span>Status: <strong>{board?.status || '…'}</strong></span>
          <span>Wallet: <strong>₹{Number(cap.walletTotal ?? capitalAmount).toLocaleString('en-IN')}</strong></span>
          <span>Free: <strong>₹{Number(cap.capitalAvailable || 0).toLocaleString('en-IN')}</strong></span>
          <span>In use: <strong>₹{Number(cap.capitalInUse || 0).toLocaleString('en-IN')}</strong></span>
          <span>Gross: <strong>₹{Number(board?.dailyGrossPnl || 0).toFixed(2)}</strong></span>
          <span>Charges: <strong>₹{Number(board?.dailyCharges || 0).toFixed(2)}</strong></span>
          <span>Day net: <strong>₹{Number(board?.dailyPnl || 0).toFixed(2)}</strong></span>
          <span>Open: <strong>{basket.slotsUsed || 0}</strong> names</span>
          <span style={{ opacity: 0.85 }}>{board?.lastAction || ''}</span>
        </div>

        <TradingActionBoard
          buy={buy}
          hold={[]}
          sell={[]}
          sellActive={[]}
          sellHistory={soldToday}
          bestPerformersToday={null}
          stopLossToday={[]}
          shortToday={[]}
          hideSecondaryTabs
          soldReportPath="/Report?screen=bestse"
          soldReportLabel="BESTSE report →"
          sellUrgentCount={0}
          sellTodayCount={soldToday.length}
          activeStep={activeStep}
          profitLabel={`₹${Number(settings.profitTakeMinRs || 100).toFixed(0)}–₹${Number(settings.profitTakeMaxRs || 200).toFixed(0)} or 3:15`}
          profitRs={Number(settings.profitTakeMinRs || 100)}
          holdPartialMin={0}
          holdPartialMax={0}
          holdMinutes={0}
          stockQty={1}
          slotsMax={basket.slotsMax || 250}
          volatileMode
          quantSlotCount={basket.slotsUsed || 0}
          liveStatus={board?.marketOpen ? 'live' : 'closed'}
          fullyAutomated
          slotsFree={basket.slotsFree}
          holdSlotCount={0}
          buyTabHint="Scanning all ₹100–₹2000 day-gainers · qty 1 each · sell at ₹100–₹200 profit"
        />
      </main>
    </div>
  );
}
